import { Box, Text, HStack, Stack } from "@chakra-ui/layout";
import { ReactNode } from "react";

import { G1Button } from "@components/G1Button";
import { $g1Color } from "@/theme/G1Style";
import { G1Icon } from "@/utils/icons";

import { Step } from "./SignupTypes";
import { StepSummary } from "./StepSummary";

interface StepConfig {
    key: Step;
    label: string;
    element: ReactNode;
    footer?: ReactNode;
}

interface RegisterStepperProps {
    steps: StepConfig[];
    stepOrder: Step[];
    currentStep: Step;
    completedSteps: Set<Step>;
    summaryProps: Omit<React.ComponentProps<typeof StepSummary>, "step">;
    isSubscriptionSucceeded: boolean;
    validateStep: (step: Step) => boolean;
    onStepClick: (step: Step) => void;
    onNextStep: (current: Step, next: Step) => void;
}

export default function RegisterStepper(props: RegisterStepperProps) {
    const {
    steps,
    stepOrder,
    currentStep,
    completedSteps,
    summaryProps,
    isSubscriptionSucceeded,
    validateStep,
    onStepClick,
    onNextStep,
} = props;

    return (
        <>
            {steps.map(({ key, label, element, footer }, index) => {
                const isCompleted = completedSteps.has(key);
                const isActive = currentStep === key;
                const isLocked = !isActive && !isCompleted;

                return (
                    <Box
                        key={key}
                        width="100%"
                        borderWidth="2px"
                        borderRadius="lg"
                        borderColor={isCompleted ? "green.300" : isActive ? "blue.400" : "gray.200"}
                        bg={$g1Color("connection.form.bg")}
                        color={$g1Color("connection.form.write")}
                        opacity={isLocked ? 0.5 : 1}
                        transition="all 0.3s"
                    >
                        <HStack
                            px={6} py={4}
                            cursor={isCompleted && !isSubscriptionSucceeded ? "pointer" : "default"}
                            onClick={() => isCompleted && !isSubscriptionSucceeded && onStepClick(key)}
                            justify="space-between"
                        >
                            <HStack spacing={3}>
                                <Box
                                    borderRadius="full" w={6} h={6}
                                    display="flex" alignItems="center" justifyContent="center"
                                    bg={isCompleted ? "green.400" : isActive ? "blue.400" : "gray.300"}
                                    color="white" fontSize="xs" fontWeight="bold"
                                >
                                    {isCompleted ? <G1Icon.Check /> : index + 1}
                                </Box>
                                <Text
                                    fontWeight="semibold" fontSize="2xl"
                                    color={isCompleted ? "green.500" : isActive ? "blue.500" : "gray.400"}
                                >
                                    {label}
                                </Text>
                            </HStack>
                            {isCompleted && !isSubscriptionSucceeded && !isActive && <G1Icon.Down />}
                        </HStack>

                        {isCompleted && !isActive && (
                            <StepSummary step={key} {...summaryProps} />
                        )}

                        {isActive && (
                            <Box px={6} pb={6}>
                                {element}
                                {key !== "confirmation" && (
                                    <Stack width="100%" direction={{ base: "column", md: "row" }} justifyContent="space-between" align={{ base: "stretch", md: "center" }} mt={4}>
                                        <Box>{footer}</Box>
                                        <G1Button
                                            component="connection"
                                            variant="ok"
                                            title="Continuer"
                                            isDisabled={!validateStep(key)}
                                            onClick={() => onNextStep(key, stepOrder[index + 1])}
                                        />
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            })}
        </>
    );
}